import { FC, useState, useEffect } from 'react';
import { Autocomplete, TextField, ListItemText } from '@mui/material';
import { useQuery } from 'react-query';
import { IPunchlistAssignToLookupSlim } from '../../../models';
import { getPunchlistAssignToLookupSlim } from '../../../fetch';

interface PunchlistItemAssignedToSlimProps {
  value: string | null;
  handleChange: (value: string) => void;
}

export const PunchlistItemAssignedToSlim: FC<PunchlistItemAssignedToSlimProps> = ({
  value,
  handleChange
}) => {
  const { isLoading, data: assignToData } = useQuery<IPunchlistAssignToLookupSlim[], Error>(
    'assignToItems',
    () => getPunchlistAssignToLookupSlim({ includeGeneralAssignment: true })
  );

  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    setInputValue(value ?? ''); // Update inputValue when value prop changes
  }, [value]);

  const [filteredOptions, setFilteredOptions] = useState<IPunchlistAssignToLookupSlim[]>([]);

  useEffect(() => {
    if (assignToData) {
      const lowercasedInput = inputValue.toLowerCase();
      const newFilteredOptions = assignToData.filter(option =>
        lowercasedInput.split(' ').every(part => option.description.toLowerCase().includes(part))
      );
      setFilteredOptions(newFilteredOptions);
    }
  }, [inputValue, assignToData]);

  const selectedValue = assignToData?.find(item => item.value === value) ?? null;

  return (
    <Autocomplete
      disabled={isLoading}
      options={filteredOptions}
      getOptionLabel={(option: IPunchlistAssignToLookupSlim) => option.description}
      value={selectedValue}
      inputValue={selectedValue ? selectedValue.description : inputValue}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      onChange={(event, newValue: IPunchlistAssignToLookupSlim | null) => {
        handleChange(newValue ? newValue.value : '');
      }}
      filterOptions={options => options}
      renderInput={params => (
        <TextField {...params} label={isLoading ? 'Loading...' : 'Assigned To'} size="small" />
      )}
      renderOption={(props, option) => (
        <li {...props} key={option.value}>
          <ListItemText>{option.description}</ListItemText>
        </li>
      )}
      filterSelectedOptions
      clearOnEscape
      autoHighlight
      selectOnFocus
      clearOnBlur={false}
    />
  );
};

export default PunchlistItemAssignedToSlim;
